import React, { useState, useEffect, useCallback, memo } from 'react';
import PageTitle from '../../components/PageTitle';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Modal, Button, Alert, Form, InputGroup, Dropdown } from 'react-bootstrap';
import PatientNotes from '../../components/PatientNotes';
import WhatsappMessageModal from '../../components/WhatsappMessageModal';
import OperationModal from '../../components/OperationModal';
import IraqiPhoneValidator from '../../utils/IraqiPhoneValidator';
import './PatientDetails.css';

// Memoized chat message component to prevent unnecessary re-renders
const ChatMessage = memo(({ role, content }) => (
    <div className={`mb-3 d-flex ${role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
        <div 
            className={`p-3 rounded-3 ${role === 'user' ? 'bg-primary text-white' : 'bg-light'}`}
            style={{ maxWidth: '80%' }}
        >
            {role === 'assistant' ? (
                <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                        // Custom styling for markdown elements
                        h1: ({children}) => <h5 className="fw-bold mb-2">{children}</h5>,
                        h2: ({children}) => <h6 className="fw-bold mb-2">{children}</h6>,
                        h3: ({children}) => <strong className="d-block mb-1">{children}</strong>,
                        p: ({children}) => <p className="mb-2">{children}</p>,
                        ul: ({children}) => <ul className="mb-2 ps-3">{children}</ul>,
                        ol: ({children}) => <ol className="mb-2 ps-3">{children}</ol>,
                        li: ({children}) => <li className="mb-1">{children}</li>,
                        strong: ({children}) => <strong>{children}</strong>,
                        em: ({children}) => <em>{children}</em>,
                        code: ({children}) => <code className="bg-secondary bg-opacity-25 px-1 rounded">{children}</code>,
                        pre: ({children}) => <pre className="bg-secondary bg-opacity-25 p-2 rounded small">{children}</pre>,
                        table: ({children}) => <table className="table table-sm table-bordered">{children}</table>,
                        blockquote: ({children}) => <blockquote className="border-start border-3 ps-3 text-muted">{children}</blockquote>
                    }}
                >
                    {content}
                </ReactMarkdown>
            ) : (
                content
            )}
        </div>
    </div>
));

// Memoized chat input component
const ChatInput = memo(({ 
    value, 
    onChange, 
    onSubmit, 
    isSending, 
    disabled 
}) => (
    <Form onSubmit={onSubmit} className="w-100">
        <InputGroup>
            <Form.Control
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Type your message..."
                disabled={disabled}
            />
            <Button 
                variant="primary" 
                type="submit"
                disabled={!value.trim() || disabled}
            >
                {isSending ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Sending...
                    </>
                ) : (
                    <>
                        <i className="fas fa-paper-plane me-1"></i>
                        Send
                    </>
                )}
            </Button>
        </InputGroup>
    </Form>
));

// Chat modal component
const ChatModal = memo(({ 
    show, 
    onHide, 
    messages, 
    isSending, 
    chatError, 
    onSendMessage, 
    newMessage, 
    onMessageChange,
    patientName
}) => {
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        onSendMessage(e);
    }, [onSendMessage]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-comment-medical me-2"></i>
                    AI Chat - {patientName || 'Patient'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: '400px', maxHeight: '60vh', overflowY: 'auto' }}>
                {messages.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        <i className="fas fa-comments fa-3x mb-3"></i>
                        <p>Ask me anything about this patient's medical information.</p>
                    </div>
                ) : (
                    <div className="chat-messages">
                        {messages.map((msg, index) => (
                            <ChatMessage 
                                key={index} 
                                role={msg.role} 
                                content={msg.content} 
                            />
                        ))}
                        {isSending && (
                            <div className="text-center my-2">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Sending...</span>
                                </div>
                            </div>
                        )}
                        {chatError && (
                            <div className="alert alert-danger">
                                <i className="fas fa-exclamation-circle me-2"></i>
                                {chatError}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <ChatInput
                    value={newMessage}
                    onChange={onMessageChange}
                    onSubmit={handleSubmit}
                    isSending={isSending}
                    disabled={isSending}
                />
            </Modal.Footer>
        </Modal>
    );
});

export default function PatientDetails() {
    const { id } = useParams();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [analysis, setAnalysis] = useState('');
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState('');
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(true);
    const [notesError, setNotesError] = useState('');

    const [whatsappMessages, setWhatsappMessages] = useState([]);
    const [whatsappMessagesLoading, setWhatsappMessagesLoading] = useState(true);
    const [whatsappMessagesError, setWhatsappMessagesError] = useState('');
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [noteForm, setNoteForm] = useState({
        description: '',
        type: 'generic',
        fee: '',
        date: (() => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        })(), // Default to today's local date
        lmp: ''
    });

    const [showMessageModal, setShowMessageModal] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [messageForm, setMessageForm] = useState({
        body: '',
        time_to_send: (() => {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            const yyyy = now.getFullYear();
            const mm = pad(now.getMonth() + 1);
            const dd = pad(now.getDate());
            return `${yyyy}-${mm}-${dd}`;
        })()
    });
    const [messageSubmitting, setMessageSubmitting] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [messageSuccess, setMessageSuccess] = useState('');

    const [messageTemplates, setMessageTemplates] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const [showMessageDeleteConfirm, setShowMessageDeleteConfirm] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const [messageDeleteError, setMessageDeleteError] = useState('');
    const [isDeletingMessage, setIsDeletingMessage] = useState(false);
    
    // Chat state
    const [showChat, setShowChat] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [chatError, setChatError] = useState('');
    
    // Operations state
    const [operations, setOperations] = useState([]);
    const [operationsLoading, setOperationsLoading] = useState(false);
    const [operationsError, setOperationsError] = useState('');
    const [showOperationModal, setShowOperationModal] = useState(false);
    const [editingOperation, setEditingOperation] = useState(null);
    const [operationForm, setOperationForm] = useState({ type: '', date: '', hospital: '', clinical_notes: '' });
    const [operationSubmitting, setOperationSubmitting] = useState(false);
    const [operationError, setOperationError] = useState('');
    const [operationSuccess, setOperationSuccess] = useState('');
    const [showOperationDeleteConfirm, setShowOperationDeleteConfirm] = useState(false);
    const [deletingOperationId, setDeletingOperationId] = useState(null);
    const [operationDeleteError, setOperationDeleteError] = useState('');
    const [isDeletingOperation, setIsDeletingOperation] = useState(false);
    
    // Handle form submission for prescription
    const handleSubmitPrescription = async (prescriptionData, currentPrescription) => {
        if (!currentPrescription) return;

        const isEdit = !!currentPrescription.id;
        const noteId = currentPrescription.note_id;
        try {
            
                        const url = isEdit 
                ? `${API_URL}prescriptions/${currentPrescription.id}`
                : `${API_URL}prescriptions`;
                
            const method = isEdit ? 'PUT' : 'POST';
            
            // Create a timestamp for cache busting
            const timestamp = new Date().getTime();
            const apiUrl = new URL(url);
            apiUrl.searchParams.append('_', timestamp);
            
            const response = await fetch(apiUrl, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                                        'Accept': 'application/json'
                },
                body: JSON.stringify({
                    ...prescriptionData,
                    note_id: noteId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save prescription');
            }

            const result = await response.json();
            
            // Refresh notes to show the updated prescription
            await fetchNotes();
            
            return { success: true, data: result };
        } catch (error) {
            console.error('Error saving prescription:', error);
            return { success: false, error: error.message };
        }
    };

    // === Operations handlers ===
    const handleAddOperation = () => {
        setOperationError('');
        setOperationSuccess('');
        setEditingOperation(null);
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        setOperationForm({ type: '', date: `${yyyy}-${mm}-${dd}`, hospital: '', clinical_notes: '' });
        setShowOperationModal(true);
    };

    const handleEditOperation = (op) => {
        setOperationError('');
        setOperationSuccess('');
        setEditingOperation(op);
        const raw = op?.date ? String(op.date) : '';
        const formatted = raw.includes('T') ? raw.split('T')[0] : (raw.split(' ')[0] || raw);
        setOperationForm({
            type: op?.type || '',
            date: formatted || '',
            hospital: op?.hospital || '',
            clinical_notes: op?.clinical_notes || ''
        });
        setShowOperationModal(true);
    };

    const handleOperationInputChange = (e) => {
        const { name, value } = e.target;
        setOperationForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmitOperation = async (e) => {
        e.preventDefault();
        setOperationError('');
        setOperationSuccess('');
        setOperationSubmitting(true);
        try {
                        if (editingOperation?.id) {
                await axios.put(
                    `${API_URL}patients/${id}/operations/${editingOperation.id}`,
                    operationForm,
                    {
                        headers: {
                                                        'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                );
            } else {
                await axios.post(
                    `${API_URL}patients/${id}/operations`,
                    operationForm,
                    {
                        headers: {
                                                        'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                );
            }
            setOperationSuccess(editingOperation?.id ? 'Operation updated successfully.' : 'Operation added successfully.');
            setShowOperationModal(false);
            setEditingOperation(null);
            fetchOperations();
        } catch (err) {
            console.error(editingOperation?.id ? 'Error updating operation:' : 'Error creating operation:', err);
            const msg = err?.response?.data?.message
                || (err?.response?.data?.errors ? 'Validation failed' : null)
                || err.message
                || (editingOperation?.id ? 'Failed to update operation.' : 'Failed to create operation.');
            setOperationError(msg);
        } finally {
            setOperationSubmitting(false);
        }
    };

    const confirmDeleteOperation = (operationId) => {
        setDeletingOperationId(operationId);
        setOperationDeleteError('');
        setIsDeletingOperation(false);
        setShowOperationDeleteConfirm(true);
    };

    const handleOperationModalClose = () => {
        setShowOperationDeleteConfirm(false);
        setDeletingOperationId(null);
        setOperationDeleteError('');
        setIsDeletingOperation(false);
    };

    const handleDeleteOperation = async (operationId) => {
        if (!operationId || isDeletingOperation) return;
        try {
            setIsDeletingOperation(true);
            setOperationDeleteError('');
                        await axios.delete(`${API_URL}patients/${id}/operations/${operationId}`, {
                headers: {
                                        'Accept': 'application/json'
                }
            });
            setOperations((prev) => prev.filter((o) => o.id !== operationId));
            setShowOperationDeleteConfirm(false);
        } catch (err) {
            console.error('Error deleting operation:', err);
            setOperationDeleteError(
                err.response?.data?.message || 'Failed to delete operation. Please try again.'
            );
        } finally {
            setIsDeletingOperation(false);
            setDeletingOperationId(null);
        }
    };

    const fetchMessageTemplates = async () => {
        try {
            const res = await axios.get(`${API_URL}message-templates`, {
                headers: {
                    'Accept': 'application/json'
                }
            });

            const data = res?.data?.data || [];
            setMessageTemplates(Array.isArray(data) ? data : []);
        } catch (e) {
            if (e?.response?.status === 401) {
                navigate('/control/login');
                return;
            }
            setMessageTemplates([]);
        }
    };

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        phone_number: '',
        birthdate: '',
        description: ''
    });
    
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    axios.defaults.withCredentials = true;

    useEffect(() => {
        fetchPatient();
        fetchNotes();
        fetchWhatsappMessages();
        fetchMessageTemplates();
        fetchOperations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (showChat && messages.length === 0) {
            setMessages([{
                role: 'assistant',
                content: `Hello! I'm your AI assistant. How can I help you with ${patient?.name || 'this patient'} today?`
            }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showChat]);

    const fetchNotes = async () => {
        try {
                        const response = await axios.get(`${API_URL}patients/${id}/notes`, {
                headers: {
                                        'Accept': 'application/json'
                }
            });
            // Sort notes by date in ascending order
            const sortedNotes = [...response.data].sort((a, b) => 
                new Date(a.date) - new Date(b.date)
            );
            setNotes(sortedNotes);
        } catch (err) {
            console.error('Error fetching notes:', err);
            setNotesError('Failed to load notes. Please try again.');
        } finally {
            setNotesLoading(false);
        }
    };

    const fetchWhatsappMessages = async () => {
        try {
            setWhatsappMessagesError('');
            setWhatsappMessagesLoading(true);

                        const response = await axios.get(`${API_URL}whatsapp-messages`, {
                params: { patient_id: id, per_page: 100 },
                headers: {
                                        'Accept': 'application/json'
                }
            });

            const payload = response?.data?.data;
            const list = Array.isArray(payload) ? payload : (payload?.data || []);
            setWhatsappMessages(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Error fetching WhatsApp messages:', err);
            setWhatsappMessagesError('Failed to load messages. Please try again.');
        } finally {
            setWhatsappMessagesLoading(false);
        }
    };

    const fetchOperations = async () => {
        try {
            setOperationsError('');
            setOperationsLoading(true);
                        const res = await axios.get(`${API_URL}patients/${id}/operations`, {
                headers: {
                                        'Accept': 'application/json'
                }
            });
            const list = Array.isArray(res?.data) ? res.data : [];
            setOperations(list);
        } catch (err) {
            console.error('Error fetching operations:', err);
            setOperationsError('Failed to load operations. Please try again.');
        } finally {
            setOperationsLoading(false);
        }
    };

    const fetchPatient = async () => {
        try {
                        const response = await axios.get(`${API_URL}patients/${id}`, {
                headers: {
                                        'Accept': 'application/json'
                }
            });
            const patientData = response.data;
            setPatient(patientData);
            setEditForm({
                name: patientData.name,
                phone_number: patientData.phone_number || '',
                birthdate: patientData.birthdate || '',
                description: patientData.description || ''
            });
            setLoading(false);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setError('Unauthorized. Please login again.');
            } else if (err.response && err.response.status === 404) {
                setError('Patient not found or unauthorized.');
            } else {
                setError('Error loading patient details: ' + err.message);
            }
            setLoading(false);
        }
    };
    
    const handleEditClick = () => {
        setIsEditing(true);
    };
    
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditForm({
            name: patient.name,
            phone_number: patient.phone_number || '',
            birthdate: patient.birthdate || '',
            description: patient.description || ''
        });
    };
    
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'phone_number') {
            // Format the phone number for Iraqi format using validator
            const formattedPhone = IraqiPhoneValidator.formatPhoneNumber(value);
            setEditForm(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        } else {
            setEditForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };
    
    const handleSaveEdit = async () => {
        try {
                        const response = await axios.put(
                `${API_URL}patients/${id}`,
                editForm
            );
            
            setPatient(response.data);
            setIsEditing(false);
        } catch (err) {
            console.error('Error updating patient:', err);
            setError('Failed to update patient. Please try again.');
        }
    };

    const handleBirthdateChange = (type, value) => {
        if (!value) {
            if (type === 'year') {
                setEditForm(prev => ({ ...prev, birthdate: '' }));
            }
            return;
        }

        // Parse existing parts safely from YYYY-MM-DD without using Date()
        let y, m, d;
        if (editForm.birthdate) {
            const parts = editForm.birthdate.split('-');
            y = parseInt(parts[0]);
            m = parseInt(parts[1]); // 1-12
            d = parseInt(parts[2]);
        } else {
            const today = new Date();
            y = today.getFullYear();
            m = today.getMonth() + 1; // 1-12
            d = today.getDate();
        }

        switch (type) {
            case 'year':
                y = parseInt(value);
                break;
            case 'month':
                m = parseInt(value); // keep 1-12
                break;
            case 'day':
                d = parseInt(value);
                break;
            default:
                break;
        }

        // Validate day doesn't exceed days in selected month/year
        const daysInMonth = new Date(y, m, 0).getDate(); // using 1-based m
        if (d > daysInMonth) {
            d = daysInMonth;
        }

        const formattedDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        setEditForm(prev => ({ ...prev, birthdate: formattedDate }));
    };

    const getDaysInMonth = () => {
        if (!editForm.birthdate) return 31;
        const [yearStr, monthStr] = editForm.birthdate.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr); // 1-12
        return new Date(year, month, 0).getDate();
    };

    const calculateAge = (birthdate) => {
        if (!birthdate) return null;
        
        const today = new Date();
        const birth = new Date(birthdate);
        
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger text-center">
                    {error}
                </div>
            </div>
        );
    }

    if (!patient) {
        return null;
    }

    const handleAnalyzeWithChatGPT = async () => {
        setAnalyzing(true);
        setAnalysisError('');
        try {
                        const response = await axios.get(`${API_URL}patients/${id}/analyze-chatgpt`, {
                headers: {
                                        'Accept': 'application/json'
                }
            });
            setAnalysis(response.data.analysis);
            setShowAnalysis(true);
        } catch (err) {
            console.error('Error analyzing with ChatGPT:', err);
            setAnalysisError(
                err.response?.data?.error || 
                'Failed to analyze with ChatGPT. Please try again.'
            );
        } finally {
            setAnalyzing(false);
        }
    };

    const handleCloseAnalysis = () => {
        setShowAnalysis(false);
        setAnalysis('');
        setAnalysisError('');
    };

    const handleNoteInputChange = (e) => {
        const { name, value } = e.target;
        setNoteForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddNote = () => {
        setSubmitError('');
        setEditingNote(null);
        setNoteForm({
            description: '',
            type: 'generic',
            fee: '',
            date: (() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            })(),
            lmp: ''
        });
        setShowNoteModal(true);
    };

    const handleAddMessage = () => {
        setMessageError('');
        setMessageSuccess('');
        setEditingMessage(null);
        setMessageForm((prev) => ({
            ...prev,
            body: ''
        }));
        setShowMessageModal(true);
    };

    const handleEditMessage = (msg) => {
        setMessageError('');
        setMessageSuccess('');
        setEditingMessage(msg);

        const raw = msg?.time_to_send ? String(msg.time_to_send) : '';
        const formattedDate = raw.includes('T') ? raw.split('T')[0] : (raw.split(' ')[0] || raw);

        setMessageForm({
            body: msg?.body || '',
            time_to_send: formattedDate || messageForm.time_to_send,
        });

        setShowMessageModal(true);
    };

    const handleMessageInputChange = (e) => {
        const { name, value } = e.target;
        setMessageForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitMessage = async (e) => {
        e.preventDefault();

        setMessageError('');
        setMessageSuccess('');
        setMessageSubmitting(true);

        try {
                        if (editingMessage?.id) {
                await axios.put(
                    `${API_URL}whatsapp-messages/${editingMessage.id}`,
                    {
                        body: messageForm.body,
                        time_to_send: messageForm.time_to_send,
                    }
                );
            } else {
                await axios.post(
                    `${API_URL}whatsapp-messages`,
                    {
                        body: messageForm.body,
                        time_to_send: messageForm.time_to_send,
                        patient_id: parseInt(id)
                    }
                );
            }

            setMessageSuccess(editingMessage?.id ? 'Message updated successfully.' : 'Message scheduled successfully.');
            setShowMessageModal(false);
            setEditingMessage(null);
            fetchWhatsappMessages();
        } catch (err) {
            console.error(editingMessage?.id ? 'Error updating WhatsApp message:' : 'Error creating WhatsApp message:', err);
            const msg = err?.response?.data?.message
                || (err?.response?.data?.errors ? 'Validation failed' : null)
                || err.message
                || (editingMessage?.id ? 'Failed to update message.' : 'Failed to create message.');
            setMessageError(msg);
        } finally {
            setMessageSubmitting(false);
        }
    };

    const confirmDeleteMessage = (messageId) => {
        setDeletingMessageId(messageId);
        setMessageDeleteError('');
        setIsDeletingMessage(false);
        setShowMessageDeleteConfirm(true);
    };

    const handleMessageModalClose = () => {
        setShowMessageDeleteConfirm(false);
        setDeletingMessageId(null);
        setMessageDeleteError('');
        setIsDeletingMessage(false);
    };

    const handleDeleteMessage = async (messageId) => {
        if (!messageId || isDeletingMessage) return;

        try {
            setIsDeletingMessage(true);
            setMessageDeleteError('');

                        await axios.delete(
                `${API_URL}whatsapp-messages/${messageId}`
            );

            setWhatsappMessages((prev) => prev.filter((m) => m.id !== messageId));
            setShowMessageDeleteConfirm(false);
        } catch (err) {
            console.error('Error deleting WhatsApp message:', err);
            setMessageDeleteError(
                err.response?.data?.message ||
                'Failed to delete message. Please try again.'
            );
        } finally {
            setIsDeletingMessage(false);
            setDeletingMessageId(null);
        }
    };

    const handleEditNote = (note) => {
        setSubmitError('');
        // Include patient_id in the editingNote object
        setEditingNote({
            ...note,
            patient_id: id // Add patient_id from the URL params
        });
        // Format the date to YYYY-MM-DD for the date input
        const formattedDate = new Date(note.date).toISOString().split('T')[0];
        let formattedLmp = '';
        if (note.lmp) {
            try {
                formattedLmp = new Date(note.lmp).toISOString().split('T')[0];
            } catch (e) {
                formattedLmp = '';
            }
        }
        setNoteForm({
            description: note.description,
            appointment_note: note.appointment_note,
            type: note.type,
            date: formattedDate,
            fee: note.fee ?? '',
            lmp: formattedLmp
        });
        setShowNoteModal(true);
    };

    const handleDeleteNote = async (noteId) => {
        if (!noteId || isDeleting) return;
        
        try {
            setIsDeleting(true);
            setDeleteError('');
            
                        await axios.delete(
                `${API_URL}patients/${id}/notes/${noteId}`
            );
            
            // Remove the note from the list
            setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
            setShowDeleteConfirm(false);
        } catch (err) {
            console.error('Error deleting note:', err);
            setDeleteError(
                err.response?.data?.message || 
                'Failed to delete note. Please try again.'
            );
        } finally {
            setIsDeleting(false);
            setDeletingNoteId(null);
        }
    };

    const confirmDelete = (noteId) => {
        setDeletingNoteId(noteId);
        setDeleteError('');
        setIsDeleting(false);
        setShowDeleteConfirm(true);
    };

    const handleModalClose = () => {
        setShowDeleteConfirm(false);
        setDeletingNoteId(null);
        setDeleteError('');
    };
    
    const startNewChat = () => {
        setShowChat(true);
    };
    
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || isSending) return;
        
        const userMessage = { role: 'user', content: newMessage };
        setMessages(prev => [...prev, userMessage]);
        setNewMessage('');
        setIsSending(true);
        setChatError('');
        
        try {
                        const response = await axios.post(
                `${API_URL}chat/patient`,
                { 
                    patient_id: id,
                    message: newMessage 
                }
            );
            
            setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch (err) {
            console.error('Error sending message:', err);
            setChatError(err.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };
    
    // Helper to check if a note is being deleted
    const isNoteDeleting = (noteId) => isDeleting && deletingNoteId === noteId;
    
    const handleSubmitNote = async (e, formData = null, useGPTRevision = false) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError('');

        try {
                        let response;
            
            // Determine if we're using FormData (with files) or regular JSON
            const isFormData = formData instanceof FormData;
            
            // Create config with common headers
            const config = {
                headers: {
                                        'Accept': 'application/json',
                    // Don't set Content-Type for FormData to let the browser set it with the correct boundary
                    ...(!isFormData && { 'Content-Type': 'application/json' })
                },
                // Enable withCredentials if needed for CORS
                withCredentials: true
            };
            
            if (editingNote) {
            // Update existing note - use GPT endpoint if requested
            const shouldUseGPT = useGPTRevision && (editingNote.type === 'generic' || editingNote.type === 'appointment');
            const url = shouldUseGPT 
                ? `${API_URL}patients/${id}/notes/${editingNote.id}/update-gpt`
                : `${API_URL}patients/${id}/notes/${editingNote.id}/update`;
                
                // For FormData, let the browser set the Content-Type with the correct boundary
                const requestConfig = { ...config };
                
                // Prepare the data to send
                let dataToSend;
                
                if (isFormData) {
                    // For FormData, we need to ensure all fields are properly set
                    // Add note fields to FormData if they're not already there
                    if (!formData.has('description')) {
                        formData.append('description', noteForm.description);
                    }
                    if (!formData.has('type')) {
                        formData.append('type', noteForm.type);
                    }
                    if (!formData.has('date')) {
                        formData.append('date', noteForm.date);
                    }
                    
                    // Remove Content-Type header to let the browser set it with boundary
                    delete requestConfig.headers['Content-Type'];
                    
                    // Log FormData contents for debugging
                    console.log('FormData contents before sending:');
                    for (let pair of formData.entries()) {
                        console.log(pair[0], pair[1]);
                    }
                    
                    dataToSend = formData;
                } else {
                    // For regular JSON data
                    dataToSend = noteForm;
                }
                
                console.log('Sending request to:', url);
                console.log('Request config:', requestConfig);
                
                try {
                    // Use POST for form data, PUT for regular JSON
                    if (isFormData) {
                        response = await axios.post(
                            url,
                            dataToSend,
                            requestConfig
                        );
                    } else {
                        response = await axios.put(
                            url,
                            dataToSend,
                            requestConfig
                        );
                    }
                    console.log('Response:', response);
                } catch (error) {
                    console.error('Error response:', error.response);
                    if (error.response) {
                        console.error('Response data:', error.response.data);
                        console.error('Response status:', error.response.status);
                        console.error('Response headers:', error.response.headers);
                    } else if (error.request) {
                        console.error('No response received:', error.request);
                    } else {
                        console.error('Error message:', error.message);
                    }
                    throw error;
                }
                
                // Update the note in the list with the new media
                const updatedNotes = notes.map(note => 
                    note.id === editingNote.id ? response.data : note
                ).sort((a, b) => new Date(a.date) - new Date(b.date));
                setNotes(updatedNotes);
            } else {
                // Create new note
                // Use GPT endpoint if requested and note type is generic
                const shouldUseGPT = useGPTRevision && noteForm.type === 'generic';
                const url = shouldUseGPT 
                    ? `${API_URL}patients/${id}/notes/gpt`
                    : `${API_URL}patients/${id}/notes`;
                
                response = await axios.post(
                    url,
                    isFormData ? formData : noteForm,
                    config
                );
                
                // Add the new note to the list
                const updatedNotes = [...notes, response.data].sort((a, b) => 
                    new Date(a.date) - new Date(b.date)
                );
                setNotes(updatedNotes);
            }

            setShowNoteModal(false);
        } catch (err) {
            console.error('Error saving note:', err);
            // Check for different error message formats from the API
            const errorMessage = err.response?.data?.error || 
                                err.response?.data?.message ||
                                'Failed to save visit. Please try again.';
            
            setSubmitError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };






    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger text-center">
                    {error}
                </div>
            </div>
        );
    }

    if (!patient) {
        return null;
    }

    return (
        <div className="patient-list-container">
            <div className="container py-4">
            <PageTitle title={patient ? `${patient.name} - Patient Details` : 'Patient Details'} />
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="row justify-content-center">
                <div>
                    <div className="card patient-list-card">
                        <div className="card-header patient-list-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h2 className="mb-0">Patient Details</h2>
                                <button 
                                    type="button" 
                                    className="btn btn-sm back-button"
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fas fa-arrow-left me-1"></i> Back
                                </button>
                            </div>
                        </div>
                        <div className="card-body patient-list-body">
                            <div className="patient-info-section">
                                <div className="patient-field unified-patient-info">
                                    {/* Control Buttons - Always in top right */}
                                    {!isEditing && (
                                        <div className="control-buttons">
                                            <button 
                                                type="button" 
                                                className="control-btn edit-btn"
                                                onClick={handleEditClick}
                                                title="Edit patient details"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil" viewBox="0 0 16 16">
                                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                                                </svg>
                                                <span className="visually-hidden">Edit patient details</span>
                                            </button>
                                            <Dropdown align="end">
                                                <Dropdown.Toggle 
                                                    as="button"
                                                    className="more-actions-btn control-btn"
                                                    title="More actions"
                                                    bsPrefix="custom"
                                                >
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="modern-dropdown-menu">
                                                    <Dropdown.Item 
                                                        onClick={handleAnalyzeWithChatGPT}
                                                        className="dropdown-item-modern"
                                                        disabled={!patient?.description || analyzing}
                                                    >
                                                        {analyzing ? (
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        ) : (
                                                            <i className="fas fa-file-medical-alt me-2"></i>
                                                        )}
                                                        AI Analyzer
                                                    </Dropdown.Item>
                                                    <Dropdown.Item 
                                                        onClick={startNewChat}
                                                        className="dropdown-item-modern"
                                                        disabled={analyzing}
                                                    >
                                                        <i className="fas fa-comment-medical me-2"></i>
                                                        AI Chat
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    )}
                                    
                                    {/* Patient Information Content */}
                                    <div className="patient-info-content">
                                        {/* Name Field */}
                                        <div className="info-field">
                                            <label className="field-label">Name</label>
                                            {!isEditing ? (
                                                <div className="field-content">
                                                    {patient.name}
                                                    {patient.is_online && (
                                                        <span 
                                                            className="badge bg-success ms-2" 
                                                            style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}
                                                            title="Patient has an online account"
                                                        >
                                                            <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                                            Online
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    name="name"
                                                    className="form-control"
                                                    value={editForm.name}
                                                    onChange={handleEditChange}
                                                    placeholder="Enter patient name"
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Phone Number and Birth Date Fields - Same Line */}
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="info-field">
                                                    <label className="field-label">Phone Number</label>
                                                    {!isEditing ? (
                                                        <div className={`field-content ${!patient.phone_number ? 'empty' : ''}`}>
                                                            {patient.phone_number || 'No phone number provided'}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <input
                                                                type="tel"
                                                                name="phone_number"
                                                                className="form-control"
                                                                value={editForm.phone_number}
                                                                onChange={handleEditChange}
                                                                placeholder={IraqiPhoneValidator.getPlaceholder()}
                                                                inputMode="numeric"
                                                                title={IraqiPhoneValidator.getHelpText()}
                                                            />
                                                            <div className="form-text">{IraqiPhoneValidator.getHelpText()}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="info-field">
                                                    <label className="field-label">Birth Date</label>
                                                    {!isEditing ? (
                                                        <div className={`field-content ${!patient.birthdate ? 'empty' : ''}`}>
                                                            {patient.birthdate ? (
                                                                <>
                                                                    {new Date(patient.birthdate).toLocaleDateString('en-US', {
                                                                        timeZone: 'UTC',
                                                                    })}
                                                                    {calculateAge(patient.birthdate) !== null && (
                                                                        <span className="text-muted ms-2">
                                                                            (Age: {calculateAge(patient.birthdate)} years)
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : 'No birth date provided'}
                                                        </div>
                                                    ) : (
                                                        <div className="birthdate-selector">
                                                            <div className="row g-2">
                                                                <div className="col-4">
                                                                    <select
                                                                        className="form-select"
                                                                        value={editForm.birthdate ? parseInt(editForm.birthdate.split('-')[0]) : ''}
                                                                        onChange={(e) => handleBirthdateChange('year', e.target.value)}
                                                                    >
                                                                        <option value="">Year</option>
                                                                        {Array.from({ length: 100 }, (_, i) => {
                                                                            const year = new Date().getFullYear() - i;
                                                                            return (
                                                                                <option key={year} value={year}>
                                                                                    {year}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                </div>
                                                                <div className="col-4">
                                                                    <select
                                                                        className="form-select"
                                                                        value={editForm.birthdate ? parseInt(editForm.birthdate.split('-')[1]) : ''}
                                                                        onChange={(e) => handleBirthdateChange('month', e.target.value)}
                                                                        disabled={!editForm.birthdate || !editForm.birthdate.split('-')[0]}
                                                                    >
                                                                        <option value="">Month</option>
                                                                        {[
                                                                            'January', 'February', 'March', 'April', 'May', 'June',
                                                                            'July', 'August', 'September', 'October', 'November', 'December'
                                                                        ].map((month, index) => (
                                                                            <option key={index + 1} value={index + 1}>
                                                                                {month}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className="col-4">
                                                                    <select
                                                                        className="form-select"
                                                                        value={editForm.birthdate ? parseInt(editForm.birthdate.split('-')[2]) : ''}
                                                                        onChange={(e) => handleBirthdateChange('day', e.target.value)}
                                                                        disabled={!editForm.birthdate || !editForm.birthdate.split('-')[1]}
                                                                    >
                                                                        <option value="">Day</option>
                                                                        {Array.from({ length: getDaysInMonth() }, (_, i) => (
                                                                            <option key={i + 1} value={i + 1}>
                                                                                {i + 1}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Description Field */}
                                        <div className="info-field">
                                            <label className="field-label">Demographic, past and social history</label>
                                            {!isEditing ? (
                                                <div className={`description-field ${!patient.description ? 'empty' : ''}`}>
                                                    {patient.description || 'No description provided'}
                                                </div>
                                            ) : (
                                                <textarea
                                                    name="description"
                                                    className="form-control"
                                                    rows="8"
                                                    value={editForm.description}
                                                    onChange={handleEditChange}
                                                    placeholder="Enter patient description"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Edit Form Controls */}
                                    {isEditing && (
                                        <div className="edit-form-controls">
                                            <button 
                                                type="button" 
                                                className="cancel-btn"
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="button" 
                                                className="save-btn"
                                                onClick={handleSaveEdit}
                                                disabled={!editForm.name.trim()}
                                            >
                                                <i className="fas fa-save me-1"></i>
                                                Save Changes
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Notes Section */}
                            <PatientNotes 
                                notes={notes}
                                notesLoading={notesLoading}
                                notesError={notesError}
                                whatsappMessages={whatsappMessages}
                                whatsappMessagesLoading={whatsappMessagesLoading}
                                whatsappMessagesError={whatsappMessagesError}
                                onAddNote={handleAddNote}
                                onAddMessage={handleAddMessage}
                                onAddOperation={handleAddOperation}
                                onEditMessage={handleEditMessage}
                                onDeleteMessage={confirmDeleteMessage}
                                isMessageDeleting={(messageId) => isDeletingMessage && deletingMessageId === messageId}
                                operations={operations}
                                operationsLoading={operationsLoading}
                                operationsError={operationsError}
                                onEditOperation={handleEditOperation}
                                onDeleteOperation={confirmDeleteOperation}
                                isOperationDeleting={(operationId) => isDeletingOperation && deletingOperationId === operationId}
                                showMessageDeleteConfirm={showMessageDeleteConfirm}
                                handleMessageModalClose={handleMessageModalClose}
                                handleDeleteMessage={handleDeleteMessage}
                                deletingMessageId={deletingMessageId}
                                isDeletingMessage={isDeletingMessage}
                                messageDeleteError={messageDeleteError}
                                onEditNote={handleEditNote}
                                onDeleteNote={confirmDelete}
                                isNoteDeleting={isNoteDeleting}
                                showDeleteConfirm={showDeleteConfirm}
                                handleModalClose={handleModalClose}
                                handleDeleteNote={handleDeleteNote}
                                deletingNoteId={deletingNoteId}
                                isDeleting={isDeleting}
                                deleteError={deleteError}
                                showNoteModal={showNoteModal}
                                setShowNoteModal={setShowNoteModal}
                                onSubmit={handleSubmitNote}
                                noteForm={noteForm}
                                onInputChange={handleNoteInputChange}
                                submitting={submitting}
                                submitError={submitError}
                                editingNote={editingNote}
                                patientName={patient?.name || ''}
                                patientData={patient}
                                onSubmitPrescription={handleSubmitPrescription}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <WhatsappMessageModal
                show={showMessageModal}
                onHide={() => setShowMessageModal(false)}
                onSubmit={handleSubmitMessage}
                messageForm={messageForm}
                onInputChange={handleMessageInputChange}
                templates={messageTemplates}
                submitting={messageSubmitting}
                error={messageError}
                success={messageSuccess}
            />

            <OperationModal
                show={showOperationModal}
                onHide={() => setShowOperationModal(false)}
                onSubmit={handleSubmitOperation}
                form={operationForm}
                onInputChange={handleOperationInputChange}
                submitting={operationSubmitting}
                error={operationError}
                success={operationSuccess}
            />

            <Modal show={showMessageDeleteConfirm} onHide={handleMessageModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {messageDeleteError && (
                        <Alert variant="danger" className="mb-3">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {messageDeleteError}
                        </Alert>
                    )}
                    <p>Are you sure you want to delete this message? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleMessageModalClose}
                        disabled={isDeletingMessage}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleDeleteMessage(deletingMessageId)}
                        disabled={!deletingMessageId || isDeletingMessage}
                    >
                        {isDeletingMessage ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showOperationDeleteConfirm} onHide={handleOperationModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {operationDeleteError && (
                        <Alert variant="danger" className="mb-3">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {operationDeleteError}
                        </Alert>
                    )}
                    <p>Are you sure you want to delete this operation? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleOperationModalClose}
                        disabled={isDeletingOperation}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleDeleteOperation(deletingOperationId)}
                        disabled={!deletingOperationId || isDeletingOperation}
                    >
                        {isDeletingOperation ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Chat Modal */}
            {showChat && (
                <ChatModal 
                    show={showChat}
                    onHide={() => setShowChat(false)}
                    messages={messages}
                    isSending={isSending}
                    chatError={chatError}
                    onSendMessage={handleSendMessage}
                    newMessage={newMessage}
                    onMessageChange={setNewMessage}
                    patientName={patient?.name}
                />
            )}
            

            
            {/* Analysis Modal */}
            <Modal show={showAnalysis} onHide={handleCloseAnalysis} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>AI Analysis</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {analysisError ? (
                        <Alert variant="danger">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {analysisError}
                        </Alert>
                    ) : (
                        <div className="analysis-content" style={{ whiteSpace: 'pre-line' }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{analysis}</ReactMarkdown>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAnalysis}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            </div>
        </div>
    );
}
